/**
 * WetterBodenLayer — visual "Klartext" layer on the Dashboard.
 *
 * Translates four raw metric numbers into one-glance traffic-light
 * scales with a 1-sentence plain-language reading:
 *   - NDVI                  (0–1, satellite)
 *   - Bodenfeuchte 0–7 cm   (m³/m³)
 *   - ET₀ 7-Tage-Summe      (mm — Wasserverlust durch Verdunstung)
 *   - Temperatur 2 m        (°C, Tagesmittel)
 *
 * Aggregates over all snapshots so the Dashboard sees a fleet-wide
 * average (not a per-region drill-down — that's the RegionPlainCard's
 * job).
 */
import React, { useMemo } from "react";
import { Plant, Drop, Thermometer, CloudRain, Sparkle, Info } from "@phosphor-icons/react";
import VisualScale from "@/components/VisualScale";
import GlossaryTerm from "@/components/GlossaryTerm";

/** Average a field across snapshots, ignoring null/undefined. */
function avg(snaps, picker) {
  const vals = (snaps || []).map(picker).filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function lastWeatherValue(snap, key) {
  const arr = snap?.weather?.[key];
  if (!Array.isArray(arr) || !arr.length) return null;
  // Find last non-null value from the back
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (typeof arr[i] === "number" && !Number.isNaN(arr[i])) return arr[i];
  }
  return null;
}

function ndviKlartext(v) {
  if (v == null) return "Kein Satellitenbild verfügbar — nächste Aufnahme in den nächsten Tagen.";
  if (v < 0.25) return "Sehr schwache Vegetation — kahl, gestresst oder noch nicht ausgekeimt.";
  if (v < 0.45) return "Mäßiges Grün — Vegetation noch im Aufbau oder bereits gestresst.";
  if (v < 0.65) return "Solides Grün — Pflanzen wachsen normal, ausreichend Chlorophyll.";
  return "Kräftiges Grün — Top-Vitalität, üppige Photosynthese.";
}

function soilKlartext(v) {
  if (v == null) return "Kein Bodenfeuchte-Wert verfügbar.";
  if (v < 0.12) return "Sehr trocken — Pflanzen sind gestresst, Bewässerung erwägen.";
  if (v < 0.20) return "Trockener als optimal — Bewässerung bald sinnvoll.";
  if (v < 0.32) return "Im grünen Bereich — Pflanzen haben genug Wasser.";
  if (v < 0.40) return "Feucht — gut für Wachstum, bei Hitze unverwüstlich.";
  return "Sehr nass — Vorsicht: Wurzeln können faulen, kein zusätzliches Wasser geben.";
}

function et0Klartext(v) {
  if (v == null) return "Keine Verdunstungs-Daten.";
  if (v < 12) return "Niedrige Verdunstung — kühl und feucht, wenig Wasserverlust.";
  if (v < 22) return "Normale Verdunstung — typisch für Mai/Juni in Mitteleuropa.";
  if (v < 32) return "Hohe Verdunstung — Pflanzen schwitzen viel, Bewässerung lohnt sich.";
  return "Sehr hohe Verdunstung — Hitzewelle, Pflanzen am Limit, dringend gießen.";
}

function tempKlartext(v) {
  if (v == null) return "Keine Temperatur-Daten.";
  if (v < 5) return "Sehr kühl — Wachstum gebremst, Bienen bleiben im Stock.";
  if (v < 12) return "Kühl — moderates Wachstum, noch kein Bienenflug.";
  if (v < 20) return "Optimal — Pflanzen wachsen kräftig, Bienen sind aktiv.";
  if (v < 28) return "Warm — Wachstum stark, aber Wasserbedarf steigt.";
  return "Sehr heiß — Hitzestress wahrscheinlich, schattige Plätze suchen.";
}

export default function WetterBodenLayer({ snaps = [], testid = "wetter-boden-layer" }) {
  const data = useMemo(() => {
    const ndvi = avg(snaps, (s) => s?.metrics?.ndvi_current);
    const soil = avg(snaps, (s) => s?.metrics?.soil_moisture_current);
    const et0 = avg(snaps, (s) => s?.metrics?.et0_7d_sum);
    const temp = avg(snaps, (s) => lastWeatherValue(s, "temperature_2m_mean"));
    return { ndvi, soil, et0, temp };
  }, [snaps]);

  return (
    <section data-testid={testid} className="space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="eid-label inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-[#FFC24B]" /> Wetter & Boden · Klartext
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter mt-1 leading-[0.95]">
            Vier Zahlen, die jede:r versteht.
          </h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
            Die wichtigsten Roh-Daten in Bodenblick — übersetzt in eine Skala mit Pointer
            und einen Satz Klartext. Über alle {snaps.length || 0} Regionen gemittelt.
          </p>
        </div>
      </div>

      <div
        className="
          flex md:grid md:grid-cols-2 gap-3
          overflow-x-auto md:overflow-visible snap-x snap-mandatory
          -mx-4 md:mx-0 px-4 md:px-0
          pb-2 md:pb-0
          [&>*]:snap-start [&>*]:shrink-0 [&>*]:w-[86%] sm:[&>*]:w-[70%] md:[&>*]:w-auto
        "
        data-testid={`${testid}-grid`}
      >
        <VisualScale
          icon={Plant}
          label="Vegetationsgrün (NDVI)"
          value={data.ndvi}
          min={0} max={1}
          formatValue={(v) => v.toFixed(2)}
          zones={[
            { to: 0.25, color: "#FF5500", label: "kahl" },
            { to: 0.45, color: "#FFC24B", label: "schwach" },
            { to: 0.65, color: "#7BB661", label: "solide" },
            { to: 1.0,  color: "#00FA9A", label: "kräftig" },
          ]}
          klartext={ndviKlartext(data.ndvi)}
          source="Copernicus Sentinel-2 · 10 m Auflösung"
          testid="scale-ndvi"
          details={
            <>
              <p><strong>So entsteht die Zahl:</strong> Sentinel-2 fotografiert die Erde alle 5 Tage in 13 Wellenlängen. Pflanzen reflektieren Nah-Infrarot stark, sichtbares Rot wird absorbiert. NDVI = (NIR − Rot) ÷ (NIR + Rot) — eine Zahl zwischen −1 (Wasser/Schnee) und +1 (dichter Wald).</p>
              <p><strong>Was die Skala sagt:</strong> &lt; 0.25 = Boden oder abgestorbene Pflanzen · 0.25–0.45 = Saat im Aufbau oder gestresst · 0.45–0.65 = gesunder Bestand · &gt; 0.65 = Spitzen-Vitalität (Sommer-Wald).</p>
              <p><strong>Praxis:</strong> Bei NDVI unter 0.4 trotz Wachstumsphase: Trockenstress oder Schädlingsbefall prüfen. Drei Wochen Trend ist aussagekräftiger als ein Einzelwert.</p>
            </>
          }
        />
        <VisualScale
          icon={Drop}
          label="Bodenfeuchte 0–7 cm"
          value={data.soil}
          min={0} max={0.5}
          unit="m³/m³"
          formatValue={(v) => v.toFixed(2)}
          zones={[
            { to: 0.12, color: "#FF5500", label: "trocken" },
            { to: 0.20, color: "#FFC24B", label: "knapp" },
            { to: 0.32, color: "#7BB661", label: "okay" },
            { to: 0.40, color: "#4DA3FF", label: "feucht" },
            { to: 0.5,  color: "#8B7CF6", label: "sehr nass" },
          ]}
          klartext={soilKlartext(data.soil)}
          source="Open-Meteo · ERA5 + ICON-EU"
          testid="scale-soil"
          details={
            <>
              <p><strong>Was steckt dahinter:</strong> Volumetrische Bodenfeuchte = Wieviel Liter Wasser stecken in einem m³ Boden? Bei 0.25 m³/m³ ist jeder vierte Liter Wasser. Open-Meteo modelliert das aus Niederschlag, Verdunstung und Bodentyp.</p>
              <p><strong>Pflanzen-Welt der Werte:</strong> &lt; 0.12 = welke Stunden zählen · 0.15–0.20 = Pflanzen ziehen, kämpfen · 0.25–0.32 = Wohlfühlbereich · &gt; 0.40 = Wurzel-Belüftung leidet, Fäulnis-Risiko.</p>
              <p><strong>Tip:</strong> Bei &gt; 0.35 m³/m³ besser keine schweren Maschinen aufs Feld — der Boden verdichtet sonst auf Jahre.</p>
            </>
          }
        />
        <VisualScale
          icon={CloudRain}
          label="Verdunstung 7 Tage (ET₀)"
          value={data.et0}
          min={0} max={40}
          unit="mm"
          formatValue={(v) => v.toFixed(0)}
          zones={[
            { to: 12, color: "#4DA3FF", label: "niedrig" },
            { to: 22, color: "#7BB661", label: "normal" },
            { to: 32, color: "#FFC24B", label: "hoch" },
            { to: 40, color: "#FF5500", label: "extrem" },
          ]}
          klartext={et0Klartext(data.et0)}
          source="Open-Meteo · FAO-56 Penman-Monteith"
          testid="scale-et0"
          details={
            <>
              <p><strong>Was ist ET₀:</strong> Die theoretische Wasser-Menge, die eine ideale Wiese in den letzten 7 Tagen verdunstet hätte. Wird aus Temperatur, Wind, Luftfeuchte und Sonnenstrahlung gerechnet (Penman-Monteith-Formel, FAO-Standard).</p>
              <p><strong>Warum dich das interessiert:</strong> Vergleich mit dem Niederschlag der gleichen Tage = Wasserbilanz. Wenn ET₀ &gt; Regen, geht der Boden trocken. 22 mm bei 5 mm Regen = 17 mm Wasserdefizit.</p>
              <p><strong>Praxis:</strong> Bei &gt; 32 mm in 7 Tagen: Bewässerungs-Intervall halbieren. Mulchen reduziert ET₀ um 20–40 %.</p>
            </>
          }
        />
        <VisualScale
          icon={Thermometer}
          label="Temperatur 2 m · Tagesmittel"
          value={data.temp}
          min={-5} max={35}
          unit="°C"
          formatValue={(v) => v.toFixed(1)}
          zones={[
            { to: 5,  color: "#8B7CF6", label: "kalt" },
            { to: 12, color: "#4DA3FF", label: "kühl" },
            { to: 20, color: "#7BB661", label: "optimal" },
            { to: 28, color: "#FFC24B", label: "warm" },
            { to: 35, color: "#FF5500", label: "heiß" },
          ]}
          klartext={tempKlartext(data.temp)}
          source="Open-Meteo · DWD ICON-D2"
          testid="scale-temp"
          details={
            <>
              <p><strong>Messhöhe:</strong> 2 m über Grund, wie an einer offiziellen Wetterstation. Das ist KÄLTER als am Boden in praller Sonne (kann +15 °C sein) und WÄRMER als in 30 m Höhe (Höhenwind).</p>
              <p><strong>Was Lebewesen davon spüren:</strong> Bienen-Flug ab 12 °C · Weizen-Wachstum optimal 15–20 °C · Hitzestress beim Mais ab 32 °C · Insekten-Sterben bei &gt; 38 °C.</p>
              <p><strong>Tip:</strong> Tagesmittel kann täuschen. Bei 18 °C Mittel mit 28 °C Max + 8 °C Min: starker Tag-Nacht-Wechsel — gut für Aromastoffe in Obst und Wein.</p>
            </>
          }
        />
      </div>

      <div className="bg-white/5 border border-white/15 p-3 sm:p-4 flex items-start gap-2 text-xs text-zinc-400 leading-relaxed">
        <Info size={14} className="text-[#00FA9A] shrink-0 mt-0.5" weight="fill" />
        <span>
          <strong className="text-white">So liest du die Skalen:</strong> Der schwarze Pointer
          zeigt den Ist-Wert. Rot = ungesund/extrem, Orange = beobachten, Grün = im
          Normalbereich, Blau = sehr feucht/kühl. Tipp: Klick auf{" "}
          <GlossaryTerm word="NDVI">NDVI</GlossaryTerm>,{" "}
          <GlossaryTerm word="ET0">ET₀</GlossaryTerm> oder{" "}
          <GlossaryTerm word="Bodenfeuchte">Bodenfeuchte</GlossaryTerm> für die ausführliche
          Definition.
        </span>
      </div>
    </section>
  );
}
